import type { ApprovalAction, ApprovalRequest } from '@/lib/types';
import { BucketShell } from './_BucketShell';
import { readStructuredFields } from './_payload';

export interface RejectedViewProps {
  req: ApprovalRequest;
}

type RequestWithActions = ApprovalRequest & { actions?: ApprovalAction[] };

function readRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function latestRejectAction(req: ApprovalRequest): ApprovalAction | null {
  const withActions = req as RequestWithActions;
  const actions: ApprovalAction[] = Array.isArray(withActions.actions)
    ? withActions.actions
    : [];
  return (
    [...actions].reverse().find((action) =>
      ['reject', 'rejected'].includes(action.action),
    ) ?? null
  );
}

function rejectActionReason(action: ApprovalAction | null): string | null {
  if (!action) return null;
  const payload = readRecord(action.payload);
  const n8nBody = readRecord(payload['n8n_body']);
  const n8nStructured = readRecord(n8nBody['structured_fields']);
  const n8nBlurbParams = readRecord(n8nBody['blurb_params']);
  return (
    readString(payload['reason']) ??
    readString(payload['rejection_reason']) ??
    readString(n8nStructured['rejection_reason']) ??
    readString(n8nBlurbParams['rejection_reason'])
  );
}

export function RejectedView({ req }: RejectedViewProps) {
  const sf = readStructuredFields(req);
  const action = latestRejectAction(req);
  const reason =
    readString(sf['rejection_reason']) ?? rejectActionReason(action);
  const rejectedBy =
    readString(sf['rejected_by']) ?? readString(action?.actor_email);

  return (
    <BucketShell req={req}>
      <section className="rounded-lg border border-error-500/40 bg-error-50 p-16">
        <p className="text-xs font-semibold uppercase tracking-wide text-error-700">
          Rejected
        </p>
        <p className="mt-8 text-base font-medium text-neutral-900">{reason ?? '—'}</p>
        {rejectedBy && (
          <p className="mt-12 text-xs text-neutral-600">
            Rejected by <span className="font-medium text-neutral-800">{rejectedBy}</span>
          </p>
        )}
      </section>
    </BucketShell>
  );
}
